import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LOUNGE_NAME } from '../utils/branding';

const API = process.env.REACT_APP_API_URL || 'https://lacasadelpuro-pos-production.up.railway.app';
const GOLD = '#C9A84C';
const SURFACE = '#1A1A1A';
const CARD = '#1E1E1E';
const BORDER = '#2A2A2A';
const TEXT = '#E8E0D0';
const TEXT_DIM = '#888';
const GREEN = '#4CAF50';
const RED = '#E53935';

const SHAPES = ['','Robusto','Toro','Churchill','Corona','Gordo','Torpedo/Belicoso','Lancero/Panatela','Figurado'];
const STRENGTHS = ['','1','2','3','4','5'];

function StrengthDots({ value, max = 5 }) {
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} style={{
          width: 10, height: 10, borderRadius: '50%',
          background: i < value ? GOLD : '#333',
          border: `1px solid ${i < value ? GOLD : '#444'}`,
        }} />
      ))}
    </div>
  );
}

function TastingPanel({ cigar, onClose, onGenerate, onBreakBox, staff }) {
  const notes = cigar.tasting_notes;
  const canAct = staff?.role === 'owner' || staff?.role === 'manager';
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#151515', border: `1px solid ${BORDER}`, borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: 24, position: 'relative' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>{cigar.manufacturer}</div>
            <div style={{ color: TEXT, fontSize: 18, fontFamily: 'Georgia', marginBottom: 2 }}>{cigar.brand_line}</div>
            <div style={{ color: TEXT_DIM, fontSize: 13 }}>{cigar.vitola_name} · {cigar.shape}</div>
            {cigar.length_inches && <div style={{ color: TEXT_DIM, fontSize: 12, marginTop: 2 }}>{cigar.length_inches}" × {cigar.ring_gauge} ring</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: TEXT_DIM, fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, background: '#1A1A1A', borderRadius: 8, padding: '10px 14px', border: `1px solid ${BORDER}` }}>
            <div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Single</div>
            <div style={{ color: GOLD, fontSize: 20 }}>${parseFloat(cigar.single_price).toFixed(2)}</div>
            <div style={{ color: cigar.stock_singles > 0 ? GREEN : RED, fontSize: 11, marginTop: 4 }}>{cigar.stock_singles > 0 ? `${cigar.stock_singles} in stock` : 'Out of stock'}</div>
          </div>
          <div style={{ flex: 1, background: '#1A1A1A', borderRadius: 8, padding: '10px 14px', border: `1px solid ${BORDER}` }}>
            <div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Box of {cigar.box_quantity}</div>
            <div style={{ color: GOLD, fontSize: 20 }}>${parseFloat(cigar.box_price).toFixed(2)}</div>
            <div style={{ color: cigar.stock_boxes > 0 ? GREEN : RED, fontSize: 11, marginTop: 4 }}>{cigar.stock_boxes > 0 ? `${cigar.stock_boxes} box${cigar.stock_boxes > 1 ? 'es' : ''} in stock` : 'No boxes'}</div>
          </div>
        </div>
        {(cigar.strength || cigar.body) && (
          <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
            {cigar.strength && <div><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Strength</div><StrengthDots value={cigar.strength} /></div>}
            {cigar.body && <div><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Body</div><StrengthDots value={cigar.body} /></div>}
          </div>
        )}
        {notes ? (
          <>
            {notes.flavorTags?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Tasting Profile</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {notes.flavorTags.map((tag, i) => <span key={i} style={{ padding: '3px 10px', background: 'rgba(201,168,76,0.1)', border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 11, color: TEXT }}>{tag}</span>)}
                </div>
              </div>
            )}
            {notes.aroma && <div style={{ marginBottom: 12 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Aroma</div><div style={{ color: TEXT, fontSize: 12, lineHeight: 1.7 }}>{notes.aroma}</div></div>}
            {notes.firstThird && <div style={{ marginBottom: 12 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>First Third</div><div style={{ color: TEXT, fontSize: 12, lineHeight: 1.7 }}>{notes.firstThird}</div></div>}
            {notes.secondThird && <div style={{ marginBottom: 12 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Second Third</div><div style={{ color: TEXT, fontSize: 12, lineHeight: 1.7 }}>{notes.secondThird}</div></div>}
            {notes.finish && <div style={{ marginBottom: 12 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Finish</div><div style={{ color: TEXT, fontSize: 12, lineHeight: 1.7 }}>{notes.finish}</div></div>}
            {notes.pairings?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Pairs With</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {notes.pairings.map((p, i) => <span key={i} style={{ padding: '5px 12px', background: '#111', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, color: TEXT_DIM }}>{p.icon} {p.name}</span>)}
                </div>
              </div>
            )}
            {notes.occasion && <div style={{ marginBottom: 12 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Occasion</div><div style={{ color: TEXT, fontSize: 12 }}>{notes.occasion}</div></div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              {notes.cutRecommendation && <div style={{ flex: 1 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Cut</div><div style={{ color: TEXT, fontSize: 12 }}>{notes.cutRecommendation}</div></div>}
              {notes.restTime && <div style={{ flex: 1 }}><div style={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Rest</div><div style={{ color: TEXT, fontSize: 12 }}>{notes.restTime}</div></div>}
            </div>
            {cigar.source_name && <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${BORDER}`, color: '#555', fontSize: 10 }}>Profile: {cigar.source_name}{cigar.source_url ? <> · <a href={cigar.source_url} target="_blank" rel="noreferrer" style={{ color: '#555' }}>Source</a></> : ''}</div>}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ color: TEXT_DIM, fontSize: 13, marginBottom: 16 }}>No tasting profile yet</div>
            {canAct && <button onClick={() => onGenerate(cigar)} style={{ background: GOLD, color: '#000', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Generate Tasting Notes</button>}
          </div>
        )}
        {canAct && cigar.stock_boxes > 0 && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
            <button onClick={() => onBreakBox(cigar)} style={{ width: '100%', background: '#2A1A0A', border: '1px solid #8B4513', borderRadius: 8, color: '#CD853F', padding: '10px 16px', cursor: 'pointer', fontSize: 13 }}>
              📦 Break a Box into {cigar.box_quantity} Singles
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Cigars({ staff, hideHeader }) {
  const [cigars, setCigars] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [q, setQ] = useState('');
  const [shape, setShape] = useState('');
  const [strength, setStrength] = useState('');
  const [inStock, setInStock] = useState(false);
  const [profileStatus, setProfileStatus] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 48;
  const timer = useRef(null);

  const fetchCigars = useCallback(async (overrides = {}) => {
    setLoading(true);
    const opts = { q, shape, strength, inStock, profileStatus, page, ...overrides };
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.shape) params.set('shape', opts.shape);
    if (opts.strength) params.set('strength', opts.strength);
    if (opts.inStock) params.set('in_stock', 'true');
    if (opts.profileStatus) params.set('profile_status', opts.profileStatus);
    params.set('page', opts.page || 1);
    params.set('limit', LIMIT);
    try {
      const res = await fetch(`${API}/cigars?${params}`);
      const data = await res.json();
      setCigars(data.cigars || []);
      setTotal(data.total || 0);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [q, shape, strength, inStock, profileStatus, page]);

  useEffect(() => { fetchCigars(); }, []);

  const handleSearch = (val) => {
    setQ(val); setPage(1);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchCigars({ q: val, page: 1 }), 350);
  };

  const handleFilter = (key, val) => {
    const updates = { [key]: val, page: 1 };
    if (key === 'shape') setShape(val);
    if (key === 'strength') setStrength(val);
    if (key === 'inStock') setInStock(val);
    if (key === 'profileStatus') setProfileStatus(val);
    setPage(1);
    fetchCigars(updates);
  };

  const handleBreakBox = async (cigar) => {
    if (!window.confirm(`Break one box of ${cigar.brand_line} — ${cigar.vitola_name} into ${cigar.box_quantity} singles?`)) return;
    try {
      const res = await fetch(`${API}/cigars/${cigar.id}/break-box`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { alert(`Done! ${data.stock_boxes} boxes remaining, ${data.stock_singles} singles now available.`); fetchCigars(); setSelected(null); }
      else alert('Error: ' + data.error);
    } catch(e) { alert('Request failed'); }
  };

  const handleGenerate = async (cigar) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/cigars/${cigar.id}/tasting-notes`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { setSelected(prev => ({ ...prev, tasting_notes: data.notes, profile_status: 'live' })); fetchCigars(); }
      else alert('Error: ' + data.error);
    } catch(e) { alert('Failed to generate'); }
    setGenerating(false);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const selStyle = { background: '#111', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, padding: '6px 10px', fontSize: 12, cursor: 'pointer', outline: 'none' };
  const pageBtn = (disabled) => ({ background: disabled ? '#111' : '#2A2A2A', border: '1px solid #333', borderRadius: 6, color: disabled ? '#444' : '#CCC', padding: '6px 14px', cursor: disabled ? 'default' : 'pointer', fontSize: 13 });

  return (
    <div style={{ padding: 20, color: TEXT, fontFamily: 'Georgia' }}>
      {!hideHeader && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ color: GOLD, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>{LOUNGE_NAME}</div>
          <h1 style={{ color: TEXT, fontSize: 24, margin: 0, fontWeight: 400 }}>Cigar Catalog</h1>
          <div style={{ color: TEXT_DIM, fontSize: 13, marginTop: 4 }}>{total} cigars</div>
        </div>
      )}
      <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <input value={q} onChange={e => handleSearch(e.target.value)} placeholder="Search by brand, vitola, shape..."
            style={{ width: '100%', background: '#111', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 14px', paddingRight: 40, color: TEXT, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          {q && (
            <button
              type="button"
              onClick={() => handleSearch('')}
              aria-label="Clear search"
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none', borderRadius: 6,
                color: GOLD, fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 0,
              }}
            >×</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <select value={shape} onChange={e => handleFilter('shape', e.target.value)} style={selStyle}>
            <option value="">All Shapes</option>
            {SHAPES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={strength} onChange={e => handleFilter('strength', e.target.value)} style={selStyle}>
            <option value="">Any Strength</option>
            {STRENGTHS.filter(s => s).map(s => <option key={s} value={s}>Strength {s}</option>)}
          </select>
          <select value={profileStatus} onChange={e => handleFilter('profileStatus', e.target.value)} style={selStyle}>
            <option value="">All Profiles</option>
            <option value="live">Live Only</option>
            <option value="draft">Draft Only</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: TEXT_DIM, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={inStock} onChange={e => handleFilter('inStock', e.target.checked)} /> In Stock Only
          </label>
          <button onClick={() => { setQ(''); setShape(''); setStrength(''); setInStock(false); setProfileStatus(''); setPage(1); fetchCigars({ q:'', shape:'', strength:'', inStock:false, profileStatus:'', page:1 }); }}
            style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT_DIM, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>Clear</button>
        </div>
      </div>
      {loading ? <div style={{ textAlign: 'center', color: TEXT_DIM, padding: 60 }}>Loading catalog...</div> : cigars.length === 0 ? (
        <div style={{ textAlign: 'center', color: TEXT_DIM, padding: 60 }}>No cigars found</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 24 }}>
            {cigars.map(cigar => (
              <div key={cigar.id} onClick={() => setSelected(cigar)} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = GOLD}
                onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ color: GOLD, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>{cigar.manufacturer}</div>
                  <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: cigar.profile_status === 'live' ? 'rgba(76,175,80,0.15)' : 'rgba(100,100,100,0.2)', color: cigar.profile_status === 'live' ? GREEN : '#666', border: `1px solid ${cigar.profile_status === 'live' ? 'rgba(76,175,80,0.3)' : '#333'}` }}>
                    {cigar.profile_status === 'live' ? '● Profile' : '○ Draft'}
                  </span>
                </div>
                <div style={{ color: TEXT, fontSize: 14, marginBottom: 2 }}>{cigar.brand_line}</div>
                <div style={{ color: TEXT_DIM, fontSize: 12, marginBottom: 10 }}>{cigar.vitola_name} · {cigar.shape}</div>
                {(cigar.strength || cigar.body) && (
                  <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                    {cigar.strength && <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ color: TEXT_DIM, fontSize: 10 }}>STR</span><StrengthDots value={cigar.strength} /></div>}
                    {cigar.body && <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ color: TEXT_DIM, fontSize: 10 }}>BODY</span><StrengthDots value={cigar.body} /></div>}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <div style={{ color: GOLD, fontSize: 16 }}>${parseFloat(cigar.single_price).toFixed(2)}<span style={{ color: TEXT_DIM, fontSize: 11 }}> / stick</span></div>
                    <div style={{ color: TEXT_DIM, fontSize: 11 }}>${parseFloat(cigar.box_price).toFixed(2)} / box of {cigar.box_quantity}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: cigar.stock_singles > 0 ? GREEN : '#555', fontSize: 11 }}>{cigar.stock_singles} singles</div>
                    <div style={{ color: cigar.stock_boxes > 0 ? GREEN : '#555', fontSize: 11 }}>{cigar.stock_boxes} boxes</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, paddingBottom: 20 }}>
              <button onClick={() => { const p = Math.max(1, page-1); setPage(p); fetchCigars({ page: p }); }} disabled={page === 1} style={pageBtn(page === 1)}>← Prev</button>
              <span style={{ color: TEXT_DIM, fontSize: 13, padding: '6px 12px' }}>Page {page} of {totalPages}</span>
              <button onClick={() => { const p = Math.min(totalPages, page+1); setPage(p); fetchCigars({ page: p }); }} disabled={page === totalPages} style={pageBtn(page === totalPages)}>Next →</button>
            </div>
          )}
        </>
      )}
      {selected && <TastingPanel cigar={selected} staff={staff} onClose={() => setSelected(null)} onBreakBox={handleBreakBox} onGenerate={handleGenerate} />}
      {generating && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', color: GOLD, fontSize: 16, fontFamily: 'Georgia' }}>Generating tasting notes...</div>}
    </div>
  );
}
